import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart } from 'lucide-react';

interface CashPositionData {
  currentBalance: string;
  forecast30Day: string;
  opportunities: string;
  trend: number;
  breakdown: Array<{
    account: string;
    balance: number;
  }>;
}

interface CashPositionWidgetProps {
  data: CashPositionData;
  onAction?: (action: string, data?: any) => void;
}

export const CashPositionWidget = ({ data, onAction }: CashPositionWidgetProps) => {
  const trend = data.trend > 0 ? 'up' : 'down';
  const TrendIcon = trend === 'up' ? TrendingUp : TrendingDown;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: amount > 1000000 ? 'compact' : 'standard'
    }).format(amount);
  };

  const handleAction = (action: string, actionData?: any) => {
    onAction?.(action, actionData);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Cash Position Overview</h3>
        <Badge variant="outline" className="text-success bg-success-muted">
          <TrendIcon className="h-3 w-3 mr-1" />
          {Math.abs(data.trend)}% {trend}
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Balance</p>
                <p className="text-2xl font-bold text-foreground">{data.currentBalance}</p>
                <p className="text-xs text-muted-foreground mt-1">Available now</p>
              </div>
              <DollarSign className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">30-Day Forecast</p>
                <p className="text-2xl font-bold text-primary">{data.forecast30Day}</p>
                <p className="text-xs text-muted-foreground mt-1">Projected balance</p>
              </div>
              <TrendIcon className={`h-8 w-8 ${trend === 'up' ? 'text-success' : 'text-destructive'}`} />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Opportunities</p>
                <p className="text-2xl font-bold text-accent-foreground">{data.opportunities}</p>
                <p className="text-xs text-muted-foreground mt-1">Optimization potential</p>
              </div>
              <div className="flex flex-col items-center">
                <BarChart3 className="h-6 w-6 text-accent-foreground" />
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-xs h-6 p-1 mt-1"
                  onClick={() => handleAction('optimize')}
                >
                  View
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            Account Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.breakdown.map((account, index) => {
            const total = data.breakdown.reduce((sum, acc) => sum + acc.balance, 0);
            const percentage = ((account.balance / total) * 100).toFixed(1);
            
            return (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">{account.name}</span>
                    <span className="text-sm font-semibold text-foreground">
                      {formatCurrency(account.balance)}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        index === 0 ? 'bg-success-muted0' : 
                        index === 1 ? 'bg-accent0' : 'bg-accent0'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-muted-foreground">{percentage}% of total</span>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button 
          className="flex-1"
          onClick={() => handleAction('forecast')}
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          View 90-Day Forecast
        </Button>
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => handleAction('optimize')}
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          Optimize Cash Flow
        </Button>
        <Button 
          variant="outline"
          onClick={() => handleAction('details')}
        >
          Details
        </Button>
      </div>

      {/* AI Insights Footer */}
      <div className="bg-accent rounded-lg p-3 border border-primary/30">
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 bg-accent0 rounded-full mt-2 flex-shrink-0"></div>
          <div>
            <p className="text-sm font-medium text-primary">Windrose Insight</p>
            <p className="text-sm text-primary">
              Your cash position is strong with steady growth trends. Consider accelerating 
              receivables to unlock an additional {data.opportunities} in working capital.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};